import type { ToastPayload } from '../types/toast'

import { SfLog } from '@app/adapters/logger'
import { ColorVariants, Sizes } from '@app/shared/constants'

import { defineStore } from 'pinia'
import { ref } from 'vue'

const VALID_VARIANTS = Object.values(ColorVariants).filter(
  (v) => v !== ColorVariants.SECONDARY
) as string[]
const VALID_SIZES: string[] = [Sizes.SM, Sizes.MD, Sizes.LG]

export const useToastStore = defineStore('toast', () => {
  const toastById = ref<Record<string, ToastPayload>>({})
  const toastOrder = ref<string[]>([])

  function raiseToast(payload: ToastPayload): void {
    if (!payload.id) {
      SfLog.error('Toast payload must have an id to raise', {
        error: new Error('toast_id_missing'),
        extra: { payload },
      })

      return
    }

    const sanitizedPayload: ToastPayload = {
      ...payload,
      variant: VALID_VARIANTS.includes(payload.variant)
        ? payload.variant
        : ColorVariants.DEFAULT,
      size: VALID_SIZES.includes(payload.size) ? payload.size : Sizes.MD,
    }

    toastById.value[payload.id] = sanitizedPayload

    if (!toastOrder.value.includes(payload.id)) {
      toastOrder.value.push(payload.id)
    }
  }

  function dismissToast(id: string): void {
    if (!id) {
      SfLog.error('Toast payload must have an id to dismiss', {
        error: new Error('toast_id_missing'),
      })

      return
    }

    delete toastById.value[id]
    toastOrder.value = toastOrder.value.filter((toastId) => toastId !== id)
  }

  return {
    toastById,
    toastOrder,
    raiseToast,
    dismissToast,
  }
})
